import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";

function unitSlugToId(slug = "") {
  return String(slug).replace(/-/g, "");
}

function unitSlugToLabel(slug = "") {
  return String(slug)
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function todayKey() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function ResponderInventoryPage() {
  const { unitId } = useParams();
  const navigate = useNavigate();

  const locationId = useMemo(() => unitSlugToId(unitId), [unitId]);
  const unitLabel = useMemo(() => unitSlugToLabel(unitId), [unitId]);
  const inventoryDate = todayKey();

  const [items, setItems] = useState([]);
  const [assignedKits, setAssignedKits] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState("");
  const [kitSelected, setKitSelected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missingModal, setMissingModal] = useState(null);
  const [openNestedKit, setOpenNestedKit] = useState(null);
  const [nestedItems, setNestedItems] = useState([]);
  const [nestedKitStack, setNestedKitStack] = useState([]);

  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [locationId, inventoryDate, activeTemplateId]);

 useEffect(() => {
  if (!loading && items.length > 0) {
    saveInventoryProgress(items);
  }
}, [items]); 

useEffect(() => {
  if (assignedKits.length === 1) {
    setActiveTemplateId(assignedKits[0].id);
    setKitSelected(true);
  }
}, [assignedKits]);

  async function loadInventory() {
    try {
      setLoading(true);

      const assignmentSnap = await getDocs(collection(db, "inventoryKitAssignments"));

const matchingAssignments = assignmentSnap.docs
  .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
  .filter((a) => {
    const slug = String(a.locationSlug || "")
      .toLowerCase()
      .replace(/-/g, "");

    const name = String(a.locationName || "")
      .toLowerCase()
      .replace(/\s+/g, "");

    return (
      a.active !== false &&
      (slug === locationId.toLowerCase() || name === locationId.toLowerCase())
    );
  });

if (matchingAssignments.length === 0) {
  setAssignedKits([]);
  setItems([]);
  return;
}

const kitSnaps = await Promise.all(
  matchingAssignments.map((assignment) =>
    getDoc(doc(db, "inventoryKitTemplates", assignment.templateId))
  )
);

const loadedKits = kitSnaps
  .map((snap, index) => {
    if (!snap.exists()) return null;

    return {
      id: snap.id,
      label: matchingAssignments[index]?.templateName || snap.data().name || "Kit",
      ...snap.data(),
    };
  })
  .filter(Boolean);

if (loadedKits.length === 0) {
  setAssignedKits([]);
  setItems([]);
  return;
}

setAssignedKits(loadedKits);

const selectedTemplateId = activeTemplateId || loadedKits[0]?.id || "";

const kit = loadedKits.find((k) => k.id === selectedTemplateId);

if (!kit) {
  setItems([]);
  return;
}

      const templateItems = (kit.locations || []).flatMap((kitLocation) =>
        (kitLocation.items || []).map((item) => ({
          id: item.id,
          kitItemId: item.id,
          inventoryStockId: item.inventoryStockId || "",
          type: item.type || "stock",
          nestedTemplateId: item.nestedTemplateId || "",
          itemName: item.itemName || "",
          category: item.category || "",
          unitType: item.unitType || "each",
          checkMode:
  item.type === "checkItem"
    ? "checklist"
    : item.checkMode || item.checkType || "quantity",

passLabel:
  item.checkOptions?.[0] ||
  item.passLabel ||
  item.goodLabel ||
  "Yes",

failLabel:
  item.checkOptions?.[1] ||
  item.failLabel ||
  item.badLabel ||
  "No",
          kitLocationName: kitLocation.name || "Other",
          parLevel: Number(item.parLevel || 0),
          currentQty: "",
          countedQty: "",
          missingQty: 0,
          expirationDate: item.expirationDate || "",
          status: "unchecked",
          checkStatus: "unchecked",
        }))
      );

      const dailySnap = await getDoc(
        doc(db, "dailyInventories", `${locationId}_${inventoryDate}`)
      );

      if (!dailySnap.exists()) {
        setItems(templateItems);
        return;
      }

      const existingItems = dailySnap.data().items || [];

      const mergedItems = templateItems.map((templateItem) => {
        const existingItem = existingItems.find(
          (i) =>
            i.kitItemId === templateItem.kitItemId &&
            i.kitLocationName === templateItem.kitLocationName
        );

        if (!existingItem) return templateItem;

        return {
          ...templateItem,
          currentQty: existingItem.currentQty ?? existingItem.countedQty ?? "",
          countedQty: existingItem.countedQty ?? existingItem.currentQty ?? "",
          missingQty: existingItem.missingQty ?? 0,
          expirationDate:
            existingItem.expirationDate || templateItem.expirationDate || "",
          status: existingItem.status || "unchecked",
          checkStatus:
            existingItem.checkStatus || existingItem.status || "unchecked",
        };
      });

      setItems(mergedItems);
    } catch (err) {
      console.error("Failed to load responder inventory:", err);
      alert("Failed to load inventory.");
    } finally {
      setLoading(false);
    }
  }

  function markPass(index) {
  setItems((prev) =>
    prev.map((item, i) => {
      if (i !== index) return item;

      const isChecklist = item.checkMode === "checklist";
      const par = Number(item.parLevel || 0);

      return {
        ...item,
        currentQty: isChecklist ? item.passLabel || "Yes" : par,
        countedQty: isChecklist ? item.passLabel || "Yes" : par,
        missingQty: 0,
        status: "at_par",
        checkStatus: "par",
      };
    })
  );
}

function markFail(index) {
  const item = items[index];

  if (item.checkMode === "checklist") {
    setItems((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              currentQty: row.failLabel || "No",
              countedQty: row.failLabel || "No",
              missingQty: 1,
              status: "below_par",
              checkStatus: "below_par",
            }
          : row
      )
    );
    return;
  }

  setMissingModal({
    index,
    itemName: item.itemName,
    parLevel: Number(item.parLevel || 0),
    haveQty: item.currentQty !== "" ? String(item.currentQty) : "",
  });
}

function confirmMissing() {
  if (!missingModal) return;

  const have = Number(missingModal.haveQty);
  const par = Number(missingModal.parLevel || 0);

  if (Number.isNaN(have) || have < 0) {
    alert("Enter a valid number.");
    return;
  }

  if (missingModal.nested) {
    setNestedItems((prev) =>
      prev.map((row) => {
        if (row.id !== missingModal.itemId) return row;

        const missingQty = Math.max(0, par - have);

        return {
          ...row,
          currentQty: have,
          countedQty: have,
          missingQty,
          status: missingQty > 0 ? "below_par" : "at_par",
          checkStatus: missingQty > 0 ? "below_par" : "par",
        };
      })
    );

    setMissingModal(null);
    return;
  }

  setItems((prev) =>
    prev.map((row, i) => {
      if (i !== missingModal.index) return row;

      const missingQty = Math.max(0, par - have);

      return {
        ...row,
        currentQty: have,
        countedQty: have,
        missingQty,
        status: missingQty > 0 ? "below_par" : "at_par",
        checkStatus: missingQty > 0 ? "below_par" : "par",
      };
    })
  );

  setMissingModal(null);
}

async function loadNestedKit(item, pushCurrent = false) {
  try {
    if (pushCurrent && openNestedKit) {
      setNestedKitStack((prev) => [
        ...prev,
        {
          kit: openNestedKit,
          items: nestedItems,
        },
      ]);
    }

    const nestedSnap = await getDoc(
      doc(db, "inventoryKitTemplates", item.nestedTemplateId)
    );

    if (!nestedSnap.exists()) {
      alert("Component kit not found.");
      return;
    }

    const nestedKit = nestedSnap.data();

    const loadedItems = (nestedKit.locations || []).flatMap((loc) =>
      (loc.items || []).map((nestedItem) => ({
        ...nestedItem,
        kitItemId: nestedItem.id,
        checkMode:
  nestedItem.type === "checkItem"
    ? "checklist"
    : nestedItem.checkMode || "quantity",

passLabel:
  nestedItem.checkOptions?.[0] || "Yes",

failLabel:
  nestedItem.checkOptions?.[1] || "No",
        locationName: loc.name || "Other",
        currentQty: "",
        countedQty: "",
        missingQty: 0,
        status: "unchecked",
        checkStatus: "unchecked",
      }))
    );

    setNestedItems(
      item.nestedItems && item.nestedItems.length > 0
        ? item.nestedItems
        : loadedItems
    );

    setOpenNestedKit(item);
  } catch (err) {
    console.error(err);
    alert("Failed to open component kit.");
  }
}

function openNestedKitInventory(item) {
  setNestedKitStack([]);
  loadNestedKit(item, false);
}

function openChildNestedKit(item) {
  loadNestedKit(item, true);
}

  function getRowStyle(item) {
  const status = item.checkStatus || item.status;

  if (status === "par" || status === "at_par") {
    return { ...styles.row, ...styles.rowPar };
  }

  if (status === "below_par") {
    return { ...styles.row, ...styles.rowBelow };
  }

  return styles.row;
}

async function saveInventoryProgress(updatedItems) {
  try {
    const actionRequired = updatedItems.some(
      (item) => Number(item.missingQty || 0) > 0
    );

    await setDoc(
      doc(db, "dailyInventories", `${locationId}_${inventoryDate}`),
      {
        locationId,
        locationName: unitLabel,
        inventoryDate,
        status: "in_progress",
        actionRequired,
        items: updatedItems,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error("Failed saving progress:", err);
  }
}

  async function submitInventory() {
    const unchecked = items.filter(
      (item) =>
        !item.checkStatus ||
        item.checkStatus === "unchecked" ||
        item.status === "unchecked"
    );

    if (unchecked.length > 0) {
      alert(`Please check all items before submitting. ${unchecked.length} remaining.`);
      return;
    }

    try {
      setSaving(true);

      await setDoc(
  doc(db, "dailyInventories", `${locationId}_${inventoryDate}`),
  {
    locationId,
    locationName: unitLabel,
    inventoryDate,
    status: "submitted",
    updatedAt: serverTimestamp(),
  },
  { merge: true }
);

      alert("Inventory submitted.");
      navigate(`/responder/${unitId}`);
    } catch (err) {
      console.error("Failed to submit inventory:", err);
      alert("Failed to submit inventory.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={styles.page}>Loading inventory...</div>;
  }

  const checkedCount = items.filter(
    (item) => item.checkStatus && item.checkStatus !== "unchecked"
  ).length;

  const groupedItems = items.reduce((groups, item, index) => {
    const section = item.kitLocationName || "Other";
    if (!groups[section]) groups[section] = [];
    groups[section].push({ item, index });
    return groups;
  }, {});

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => navigate(`/responder/${unitId}`)}
        >
          Back
        </button>

        <div>
          <h1 style={styles.title}>{unitLabel} Inventory</h1>
          <p style={styles.subtitle}>
            Checked {checkedCount} of {items.length}
          </p>
        </div>

        <button
          style={styles.submitButton}
          onClick={submitInventory}
          disabled={saving}
        >
          {saving ? "Submitting..." : "Submit"}
        </button>
      </div>

      {assignedKits.length > 1 && !kitSelected && (
  <div style={styles.kitChoiceGrid}>
    {assignedKits.map((kit) => (
      <button
        key={kit.id}
        type="button"
        onClick={() => {
          setActiveTemplateId(kit.id);
          setKitSelected(true);
        }}
        style={styles.largeKitButton}
      >
        {kit.label}
      </button>
    ))}
  </div>
)}

{assignedKits.length > 1 && kitSelected && (
  <div style={{ textAlign: "center", marginBottom: 16 }}>
    <button
      onClick={() => {
        setKitSelected(false);
        setActiveTemplateId("");
        setItems([]);
      }}
      style={{
        padding: "10px 20px",
        borderRadius: 12,
        border: "none",
        background: "#475569",
        color: "#fff",
        fontWeight: 900,
        cursor: "pointer",
      }}
    >
      Back to Kit Selection
    </button>
  </div>
)}

{(assignedKits.length <= 1 || kitSelected) && (
      <div style={styles.list}>
        {Object.entries(groupedItems).map(([sectionName, sectionItems]) => (
          <section key={sectionName} style={styles.section}>
            <h2 style={styles.sectionTitle}>{sectionName}</h2>

            <div style={styles.sectionList}>
              {sectionItems.map(({ item, index }) => (
                <div
                  key={`${item.kitItemId}_${item.kitLocationName}`}
                  style={getRowStyle(item)}
                >
                  <div style={styles.itemInfo}>
                    <div style={styles.itemName}>{item.itemName}</div>

                    <div style={styles.meta}>
                      {item.category || "Uncategorized"}
                    </div>

                    <div style={styles.meta}>
                      Current:{" "}
                      {item.currentQty === "" ? "Unchecked" : item.currentQty} / PAR{" "}
                      {item.parLevel || 0}
                    </div>
                  </div>

                  <div style={styles.primaryActions}>
  {item.type === "nestedKit" ? (
    <button
      style={styles.openKitButton}
      onClick={() => openNestedKitInventory(item)}
    >
      Open Kit
    </button>
  ) : (
    <>
      <button
        style={styles.parButton}
        onClick={() => markPass(index)}
      >
        {item.checkMode === "checklist" ? item.passLabel || "Yes" : `PAR ${item.parLevel || 0}`}
      </button>

      <button
        style={styles.missingButton}
        onClick={() => markFail(index)}
      >
        {item.checkMode === "checklist"
  ? item.failLabel || "No"
  : "Missing Items"}
      </button>
    </>
  )}
</div>

                  
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      )}

      {openNestedKit && (
  <div style={styles.modalOverlay}>
    <div
      style={{
        ...styles.modal,
        width: "min(700px, 95vw)",
        maxHeight: "85vh",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 18,
        }}
      >
        <div>
          <h2 style={styles.modalTitle}>{openNestedKit.itemName}</h2>
          <p style={styles.modalSub}>Component Kit Inventory</p>
        </div>

        <button
          style={styles.cancelButton}
          onClick={() => {

            const hasMissing = nestedItems.some(
              (item) => Number(item.missingQty || 0) > 0
            );

            const completedKit = {
              ...openNestedKit,
              currentQty: Number(openNestedKit.parLevel || 1),
              countedQty: Number(openNestedKit.parLevel || 1),
              missingQty: hasMissing ? 1 : 0,
              status: hasMissing ? "below_par" : "at_par",
              checkStatus: hasMissing ? "below_par" : "par",
              nestedItems,
            };

            if (nestedKitStack.length > 0) {
              const parent = nestedKitStack[nestedKitStack.length - 1];

              const updatedParentItems = parent.items.map((row) =>
                row.id === completedKit.id ||
                row.kitItemId === completedKit.kitItemId
                  ? completedKit
                  : row
              );

              setNestedKitStack((prev) => prev.slice(0, -1));
              setOpenNestedKit(parent.kit);
              setNestedItems(updatedParentItems);
              return;
            }

            setItems((prev) =>
              prev.map((row) =>
                row.kitItemId === openNestedKit.kitItemId
                  ? completedKit
                  : row
              )
            );

            setOpenNestedKit(null);
            setNestedItems([]);
          }}
        >
          Close
        </button>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {nestedItems.map((nestedItem) => (
          <div
            key={nestedItem.id}
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 14,
              padding: 14,
              background:
                nestedItem.checkStatus === "par"
                  ? "#dcfce7"
                  : nestedItem.checkStatus === "below_par"
                  ? "#fee2e2"
                  : "#f8fafc",
              borderColor:
                nestedItem.checkStatus === "par"
                  ? "#15803d"
                  : nestedItem.checkStatus === "below_par"
                  ? "#b91c1c"
                  : "#cbd5e1",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 20, color: "#0f172a" }}>
              {nestedItem.itemName}
            </div>

            <div style={styles.meta}>{nestedItem.locationName}</div>
            <div style={styles.meta}>PAR {nestedItem.parLevel || 0}</div>

            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {nestedItem.type === "nestedKit" ? (
                <button
                  style={{
                    ...styles.openKitButton,
                    gridColumn: "1 / -1",
                  }}
                  onClick={() => openChildNestedKit(nestedItem)}
                >
                  Open Kit
                </button>
              ) : (
                <>
                  <button
                    style={styles.parButton}
                    onClick={() =>
                      setNestedItems((prev) =>
                        prev.map((row) =>
                          row.id === nestedItem.id
                            ? {
                                ...row,
                                currentQty: Number(row.parLevel || 0),
                                countedQty: Number(row.parLevel || 0),
                                missingQty: 0,
                                status: "at_par",
                                checkStatus: "par",
                              }
                            : row
                        )
                      )
                    }
                  >
                    {nestedItem.checkMode === "checklist"
                    ? nestedItem.passLabel || "Yes"
                    : `PAR ${nestedItem.parLevel || 0}`}
                  </button>

                  <button
                    style={styles.missingButton}
                    onClick={() =>
                      setMissingModal({
                        nested: true,
                        itemId: nestedItem.id,
                        itemName: nestedItem.itemName,
                        parLevel: Number(nestedItem.parLevel || 0),
                        haveQty:
                          nestedItem.currentQty !== ""
                            ? String(nestedItem.currentQty)
                            : "",
                      })
                    }
                  >
                    {nestedItem.checkMode === "checklist"
                    ? nestedItem.failLabel || "No"
                    : "Missing Items"}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)}

      {missingModal && (
  <div style={styles.modalOverlay}>
    <div style={styles.modal}>
      <h2 style={styles.modalTitle}>Missing Items</h2>

      <p style={styles.modalText}>
        {missingModal.itemName}
      </p>

      <p style={styles.modalSub}>
        PAR: {missingModal.parLevel}
      </p>

      <label style={styles.modalLabel}>
        How many do you have?
      </label>

      <input
        type="number"
        min="0"
        value={missingModal.haveQty}
        onChange={(e) =>
          setMissingModal((prev) => ({
            ...prev,
            haveQty: e.target.value,
          }))
        }
        style={styles.modalInput}
      />

      <div style={styles.modalActions}>
        <button
          style={styles.cancelButton}
          onClick={() => setMissingModal(null)}
        >
          Cancel
        </button>

        <button
          style={styles.okButton}
          onClick={confirmMissing}
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}
    </div>

  );
}

const isPhone = window.innerWidth <= 640;
const isTablet = window.innerWidth <= 1024;

const styles = {
  page: {
    minHeight: "100vh",
    padding: isPhone ? 10 : 16,
    background: "#eef4ff",
    color: "#0f172a",
  },

  header: {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "110px 1fr 130px",
    gap: 12,
    alignItems: "center",
    marginBottom: 16,
  },

  title: {
    margin: 0,
    fontSize: isPhone ? 26 : 34,
    fontWeight: 900,
    textAlign: "center",
  },

  subtitle: {
    margin: 0,
    color: "#64748b",
    textAlign: "center",
    fontWeight: 700,
  },

  backButton: {
    height: 52,
    borderRadius: 12,
    border: "none",
    background: "#475569",
    color: "#fff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
    width: "100%",
  },

  submitButton: {
    height: 52,
    borderRadius: 12,
    border: "none",
    background: "#15803d",
    color: "#fff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
    width: "100%",
  },

  list: {
    display: "grid",
    gap: isPhone ? 12 : 16,
  },

  section: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dbe4ee",
    padding: isPhone ? 10 : 14,
  },

  sectionTitle: {
    margin: "0 0 12px",
    fontSize: isPhone ? 20 : 24,
    fontWeight: 900,
    color: "#0f172a",
  },

  sectionList: {
    display: "grid",
    gap: 10,
  },

  row: {
    background: "#f8fafc",
    border: "2px solid #cbd5e1",
    borderRadius: 14,
    padding: isPhone ? 10 : 14,
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : isTablet ? "1fr 190px" : "1fr 220px",
    gap: 12,
    alignItems: "center",
  },

  rowSelected: {
    background: "#dcfce7",
    borderColor: "#15803d",
  },

 rowPar: {
  background: "#dcfce7",
  borderColor: "#15803d",
},

rowBelow: {
  background: "#fee2e2",
  borderColor: "#b91c1c",
},

primaryActions: {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  width: "100%",
},

parButton: {
  height: 52,
  border: "none",
  borderRadius: 12,
  backgroundColor: "#15803d",
  color: "#ffffff",
  fontWeight: 900,
  fontSize: 16,
  cursor: "pointer",
  WebkitAppearance: "none",
  appearance: "none",
},

missingButton: {
  height: 52,
  border: "none",
  borderRadius: 12,
  backgroundColor: "#b91c1c",
  color: "#ffffff",
  fontWeight: 900,
  fontSize: 15,
  cursor: "pointer",
  WebkitAppearance: "none",
  appearance: "none",
},

openKitButton: {
  height: 52,
  border: "none",
  borderRadius: 12,
  backgroundColor: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  fontSize: 16,
  cursor: "pointer",
  WebkitAppearance: "none",
  appearance: "none",
},

modalOverlay: {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  padding: 12,
  boxSizing: "border-box",
},

modal: {
  width: "min(420px, 92vw)",
  background: "#fff",
  borderRadius: 22,
  padding: isPhone ? 18 : 24,
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
},

modalTitle: {
  margin: 0,
  fontSize: isPhone ? 24 : 30,
  fontWeight: 900,
  color: "#0f172a",
},

modalText: {
  margin: "14px 0 4px",
  fontSize: isPhone ? 18 : 22,
  fontWeight: 800,
  color: "#0f172a",
},

modalSub: {
  margin: "0 0 18px",
  color: "#64748b",
  fontWeight: 800,
},

modalLabel: {
  display: "block",
  fontWeight: 900,
  color: "#334155",
  marginBottom: 8,
},

modalInput: {
  width: "100%",
  height: 56,
  borderRadius: 14,
  border: "1px solid #cbd5e1",
  padding: "0 14px",
  fontSize: 24,
  fontWeight: 900,
  boxSizing: "border-box",
},

modalActions: {
  display: "grid",
  gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
  gap: 12,
  marginTop: 20,
},

cancelButton: {
  height: 52,
  border: "none",
  borderRadius: 14,
  background: "#64748b",
  color: "#fff",
  fontWeight: 900,
  fontSize: 16,
  cursor: "pointer",
},

okButton: {
  height: 52,
  border: "none",
  borderRadius: 14,
  background: "#15803d",
  color: "#fff",
  fontWeight: 900,
  fontSize: 16,
  cursor: "pointer",
}, 

  itemInfo: {
    minWidth: 0,
  },

  itemName: {
    fontWeight: 900,
    fontSize: isPhone ? 18 : 20,
    color: "#0f172a",
  },

  meta: {
    color: "#64748b",
    fontWeight: 700,
    marginTop: 2,
    fontSize: 14,
  },

kitChoiceGrid: {
  display: "flex",
  justifyContent: "center",
  gap: 20,
  flexWrap: "wrap",
  marginTop: 40,
},

largeKitButton: {
  minWidth: 220,
  height: 120,
  borderRadius: 20,
  border: "none",
  background: "#15803d",
  color: "#fff",
  fontSize: 32,
  fontWeight: 900,
  cursor: "pointer",
},
};